/** Unicode range of combining diacritical marks (accents, tildes, umlauts). */
const COMBINING_DIACRITICAL_MARKS = /[\u0300-\u036f]/g;

/**
 * Normalizes a string for case- and accent-insensitive comparisons: trims
 * surrounding whitespace, lowercases it and strips diacritics by decomposing
 * each accented character (NFD) and removing the combining marks.
 *
 * Example: "  Telefónico " -> "telefonico".
 */
export const normalizeText = (value: string): string =>
    value
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(COMBINING_DIACRITICAL_MARKS, '');

/**
 * Trims the given value and always returns a string: an empty string when the
 * value is `null`, `undefined` or only whitespace. Useful for normalizing
 * optional inputs (query params, external data) into a safe, defined string.
 *
 * Example: trimToEmpty("  abc ") -> "abc"; trimToEmpty(undefined) -> "".
 */
export const trimToEmpty = (value?: string | null): string =>
    value?.trim() ?? '';

/**
 * Parses a textual boolean flag (e.g. a query param) into a boolean. Returns
 * `true` only when the trimmed, lowercased value equals `"true"`; any other
 * value (including `null`, `undefined` or empty) resolves to `false`.
 *
 * Example: parseBooleanFlag("true") -> true; parseBooleanFlag("false") -> false.
 */
export const parseBooleanFlag = (value?: string | null): boolean =>
    trimToEmpty(value).toLowerCase() === 'true';
